import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, createBillingCheckout, createBillingPortal, fetchAccount, fetchBillingState, fetchChatHistory, fetchDeviceStatus, getWorkOsStartUrl, redeemQrToken, refreshAccessToken, sendChatMessage, uploadAttachment, verifyWorkOsCode, } from './api';
import { loadStoredSession, persistSession } from './session';
const THREAD_ID = 'basket-phone-thread';
export default function App() {
    const [route, setRoute] = useState(() => parseRoute());
    const [session, setSession] = useState(() => loadStoredSession());
    const [user, setUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [deviceStatus, setDeviceStatus] = useState([]);
    const [composer, setComposer] = useState('');
    const [sending, setSending] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [activeTab, setActiveTab] = useState('chat');
    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);
    const [entitled, setEntitled] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [usageCooldown, setUsageCooldown] = useState({ until: 0, reason: null });
    const [errorCooldown, setErrorCooldown] = useState({ until: 0, reason: null });
    const [cooldownNow, setCooldownNow] = useState(() => Date.now());
    const previousCooldownActiveRef = useRef(false);
    const usageCooldownUntil = usageCooldown.until > cooldownNow ? usageCooldown.until : 0;
    const errorCooldownUntil = errorCooldown.until > cooldownNow ? errorCooldown.until : 0;
    const cooldownUntil = Math.max(usageCooldownUntil, errorCooldownUntil);
    const cooldownReason = cooldownUntil === errorCooldownUntil ? errorCooldown.reason : usageCooldown.reason;
    const cooldownRemainingMs = Math.max(0, cooldownUntil - cooldownNow);
    const cooldownActive = cooldownRemainingMs > 0;
    useEffect(() => {
        const onPop = () => setRoute(parseRoute());
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);
    useEffect(() => {
        persistSession(session);
    }, [session]);
    useEffect(() => {
        if (!session && route.route === 'chat') {
            replacePath('/login');
            setRoute({ route: 'login' });
        }
        else if (session && (route.route === 'login')) {
            replacePath('/chat');
            setRoute({ route: 'chat' });
        }
    }, [session, route]);
    useEffect(() => {
        if (route.route !== 'qr')
            return;
        let cancelled = false;
        setStatusMessage('Linking your phone…');
        (async () => {
            try {
                const result = await redeemQrToken(route.token);
                if (cancelled)
                    return;
                setSession(toStoredSession(result.session));
                setStatusMessage('Linked! Loading chat…');
                replacePath('/chat');
                setRoute({ route: 'chat' });
            }
            catch (err) {
                if (!cancelled) {
                    setStatusMessage(err instanceof Error ? err.message : 'Could not complete QR login.');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [route]);
    useEffect(() => {
        if (route.route !== 'login' || session)
            return;
        const code = new URLSearchParams(window.location.search).get('code');
        if (!code)
            return;
        let cancelled = false;
        setStatusMessage('Signing in…');
        (async () => {
            try {
                const result = await verifyWorkOsCode(code);
                if (cancelled)
                    return;
                setSession(toStoredSession(result.session));
                setUser(result.user);
                setStatusMessage('Signed in.');
                replacePath('/chat');
                setRoute({ route: 'chat' });
            }
            catch (err) {
                if (cancelled)
                    return;
                setStatusMessage(err instanceof Error ? err.message : 'Login failed.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [route, session]);
    const ensureAccessToken = useCallback(async () => {
        if (!session)
            return null;
        if (session.refreshExpiresAt <= Date.now()) {
            setSession(null);
            setUser(null);
            setStatusMessage('Session expired. Sign in again.');
            return null;
        }
        if (session.accessExpiresAt - 5000 > Date.now())
            return session.accessToken;
        try {
            const refreshed = await refreshAccessToken(session.refreshToken);
            const next = {
                ...session,
                accessToken: refreshed.accessToken,
                accessExpiresAt: Date.now() + refreshed.expiresIn * 1000,
            };
            setSession(next);
            return next.accessToken;
        }
        catch (err) {
            console.error(err);
            setSession(null);
            setStatusMessage('Session expired. Sign in again.');
            return null;
        }
    }, [session]);
    const syncUsageCooldown = useCallback((nextUsage) => {
        const next = deriveCooldownFromUsage(nextUsage);
        if (!next) {
            setUsageCooldown({ until: 0, reason: null });
            return;
        }
        setUsageCooldown(next);
    }, []);
    const setErrorCooldownFromApi = useCallback((err) => {
        const now = Date.now();
        const retryAt = Number(err.retryAt || 0);
        const retryAfterSeconds = Number(err.retryAfterSeconds || 0);
        const until = Number.isFinite(retryAt) && retryAt > now
            ? retryAt
            : retryAfterSeconds > 0
                ? now + Math.ceil(retryAfterSeconds) * 1000
                : 0;
        if (until <= now)
            return;
        setErrorCooldown({ until, reason: err.reason || 'usage_limit' });
    }, []);
    useEffect(() => {
        if (!cooldownActive) {
            setCooldownNow(Date.now());
            return;
        }
        setCooldownNow(Date.now());
        const timer = window.setInterval(() => {
            setCooldownNow(Date.now());
        }, 1000);
        return () => window.clearInterval(timer);
    }, [cooldownActive]);
    useEffect(() => {
        if (usageCooldown.until > 0 && usageCooldown.until <= cooldownNow) {
            setUsageCooldown({ until: 0, reason: null });
        }
        if (errorCooldown.until > 0 && errorCooldown.until <= cooldownNow) {
            setErrorCooldown({ until: 0, reason: null });
        }
    }, [cooldownNow, usageCooldown.until, errorCooldown.until]);
    useEffect(() => {
        if (previousCooldownActiveRef.current && !cooldownActive) {
            setStatusMessage('Cooldown finished — you can send messages again.');
        }
        previousCooldownActiveRef.current = cooldownActive;
    }, [cooldownActive]);
    useEffect(() => {
        if (!session) {
            setUser(null);
            setMessages([]);
            setDeviceStatus([]);
            return;
        }
        let cancelled = false;
        (async () => {
            const token = await ensureAccessToken();
            if (!token || cancelled)
                return;
            try {
                const data = await fetchAccount(token);
                if (!cancelled)
                    setUser(data.user);
                if (!cancelled)
                    setDeviceStatus(data.runtimes);
                if (!cancelled)
                    setEntitled(Boolean(data.entitled));
                if (!cancelled)
                    setSubscription(data.subscription || null);
                if (!cancelled)
                    setUsage(data.usage || null);
                if (!cancelled)
                    syncUsageCooldown(data.usage || null);
            }
            catch (err) {
                console.error(err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [session, ensureAccessToken, syncUsageCooldown]);
    useEffect(() => {
        if (!session)
            return;
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            const token = await ensureAccessToken();
            if (!token || cancelled)
                return;
            try {
                const hist = await fetchChatHistory(token, THREAD_ID);
                if (!cancelled)
                    setMessages(hist);
            }
            catch {
                // ignore
            }
            finally {
                if (!cancelled)
                    timer = window.setTimeout(poll, 3500);
            }
        };
        poll();
        return () => {
            cancelled = true;
            if (timer)
                window.clearTimeout(timer);
        };
    }, [session, ensureAccessToken]);
    useEffect(() => {
        if (!session)
            return;
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            const token = await ensureAccessToken();
            if (!token || cancelled)
                return;
            try {
                const state = await fetchBillingState(token, false);
                if (!cancelled)
                    setEntitled(Boolean(state.entitled));
                if (!cancelled)
                    setSubscription(state.subscription || null);
                if (!cancelled)
                    setUsage(state.usage || null);
                if (!cancelled)
                    syncUsageCooldown(state.usage || null);
            }
            catch {
                // ignore
            }
            finally {
                if (!cancelled)
                    timer = window.setTimeout(poll, 60000);
            }
        };
        poll();
        return () => {
            cancelled = true;
            if (timer)
                window.clearTimeout(timer);
        };
    }, [session, ensureAccessToken, syncUsageCooldown]);
    useEffect(() => {
        if (!session)
            return;
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            const token = await ensureAccessToken();
            if (!token || cancelled)
                return;
            try {
                const status = await fetchDeviceStatus(token);
                if (!cancelled)
                    setDeviceStatus(status);
            }
            catch {
                // ignore
            }
            finally {
                if (!cancelled)
                    timer = window.setTimeout(poll, 10000);
            }
        };
        poll();
        return () => {
            cancelled = true;
            if (timer)
                window.clearTimeout(timer);
        };
    }, [session, ensureAccessToken]);
    const handleSend = useCallback(async (text) => {
        if (!text.trim() && pendingFiles.length === 0)
            return;
        if (cooldownActive) {
            setStatusMessage(`Cooldown active (${formatCooldownReason(cooldownReason)}). Try again in ${formatDurationShort(cooldownRemainingMs)}.`);
            return;
        }
        const token = await ensureAccessToken();
        if (!token)
            return;
        const normalizedText = text.trim();
        const outgoing = normalizedText || `Please review these files: ${pendingFiles.map((f) => f.file.name).join(', ')}`;
        setSending(true);
        setStatusMessage(pendingFiles.length > 0 ? 'Uploading attachments…' : 'Sending…');
        setMessages((prev) => [
            ...prev,
            {
                role: 'user',
                content: outgoing,
                timestamp: new Date().toISOString(),
                attachments: pendingFiles.map((item) => ({
                    name: item.file.name,
                    sizeBytes: item.file.size,
                    contentType: item.file.type || 'application/octet-stream',
                    previewUrl: item.previewUrl,
                })),
            },
        ]);
        try {
            const uploaded = pendingFiles.length > 0
                ? await Promise.all(pendingFiles.map((item) => uploadAttachment(token, item.file, THREAD_ID)))
                : [];
            await sendChatMessage(token, THREAD_ID, outgoing, uploaded);
            setComposer('');
            setPendingFiles([]);
            setStatusMessage('Queued for desktop…');
            try {
                const state = await fetchBillingState(token, false);
                setEntitled(Boolean(state.entitled));
                setSubscription(state.subscription || null);
                setUsage(state.usage || null);
                syncUsageCooldown(state.usage || null);
            }
            catch {
                // ignore follow-up usage sync failure
            }
        }
        catch (err) {
            console.error(err);
            if (err instanceof ApiError) {
                setErrorCooldownFromApi(err);
            }
            setStatusMessage(err instanceof Error ? err.message : 'Failed to send message.');
        }
        finally {
            setSending(false);
        }
    }, [cooldownActive, cooldownReason, cooldownRemainingMs, ensureAccessToken, pendingFiles, setErrorCooldownFromApi, syncUsageCooldown]);
    const addFiles = useCallback((incoming) => {
        if (!incoming || incoming.length === 0)
            return;
        const next = Array.from(incoming);
        setPendingFiles((prev) => {
            const merged = [...prev];
            for (const f of next) {
                const exists = merged.some((p) => p.file.name === f.name && p.file.size === f.size && p.file.lastModified === f.lastModified);
                if (!exists) {
                    merged.push({
                        file: f,
                        previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
                    });
                }
            }
            return merged.slice(0, 8);
        });
    }, []);
    const removePendingFile = useCallback((idx) => {
        setPendingFiles((prev) => {
            const target = prev[idx];
            if (target?.previewUrl)
                URL.revokeObjectURL(target.previewUrl);
            return prev.filter((_, i) => i !== idx);
        });
    }, []);
    const handleLogin = useCallback(async (email) => {
        setStatusMessage('Redirecting to secure login…');
        const returnTo = `${window.location.origin}/login`;
        window.location.assign(getWorkOsStartUrl(email.trim(), returnTo));
    }, []);
    const content = useMemo(() => {
        if (route.route === 'qr') {
            return _jsx(QrLinkNotice, { token: route.token, message: statusMessage || 'Linking this browser to Picnic…' });
        }
        if (!session) {
            return _jsx(LoginView, { onLogin: handleLogin, statusMessage: statusMessage });
        }
        return (_jsx(ChatView, { user: user, messages: messages, deviceStatus: deviceStatus, activeTab: activeTab, onSelectTab: setActiveTab, subscription: subscription, usage: usage, entitled: entitled, composer: composer, setComposer: setComposer, pendingFiles: pendingFiles, onAddFiles: addFiles, onRemoveFile: removePendingFile, sending: sending, onSend: handleSend, statusMessage: statusMessage, onRefreshSubscription: async () => {
                const token = await ensureAccessToken();
                if (!token)
                    return;
                const state = await fetchBillingState(token, true);
                setEntitled(Boolean(state.entitled));
                setSubscription(state.subscription || null);
                setUsage(state.usage || null);
                syncUsageCooldown(state.usage || null);
                setStatusMessage(state.entitled ? 'Subscription is active.' : 'Subscription not active.');
            }, onBuySubscription: async () => {
                const token = await ensureAccessToken();
                if (!token)
                    return;
                const { checkoutUrl } = await createBillingCheckout(token);
                if (checkoutUrl)
                    window.location.assign(checkoutUrl);
            }, onManageBilling: async () => {
                const token = await ensureAccessToken();
                if (!token)
                    return;
                const { customerPortalUrl } = await createBillingPortal(token);
                if (customerPortalUrl)
                    window.open(customerPortalUrl, '_blank', 'noopener,noreferrer');
            }, cooldownActive: cooldownActive, cooldownRemainingMs: cooldownRemainingMs, cooldownReason: cooldownReason }));
    }, [
        route,
        session,
        user,
        messages,
        deviceStatus,
        composer,
        pendingFiles,
        sending,
        handleSend,
        addFiles,
        removePendingFile,
        handleLogin,
        statusMessage,
        ensureAccessToken,
        cooldownActive,
        cooldownRemainingMs,
        cooldownReason,
        syncUsageCooldown,
    ]);
    const modeClass = session ? 'mode-chat' : 'mode-login';
    return (_jsx("div", { className: `basket-app ${modeClass}`, children: content }));
}
function LoginView({ onLogin, statusMessage }) {
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async (event) => {
        event.preventDefault();
        if (!email.trim())
            return;
        setBusy(true);
        try {
            await onLogin(email.trim());
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsx("div", { className: "login-shell", children: _jsxs("form", { onSubmit: submit, className: "login-panel", children: [_jsxs("div", { className: "login-brand", children: [_jsx("span", { className: "dot" }), "Picnic Basket"] }), _jsx("input", { id: "login-email", type: "email", placeholder: "you@example.com", value: email, onChange: (e) => setEmail(e.target.value), required: true }), _jsx("button", { className: "primary", type: "submit", disabled: busy, children: busy ? 'Signing in…' : 'Enter' }), statusMessage && _jsx("p", { className: "status subtle", children: statusMessage })] }) }));
}
function ChatView(props) {
    const { user, messages, deviceStatus, activeTab, onSelectTab, subscription, usage, entitled, composer, setComposer, pendingFiles, onAddFiles, onRemoveFile, sending, onSend, statusMessage, onRefreshSubscription, onBuySubscription, onManageBilling, cooldownActive, cooldownRemainingMs, cooldownReason, } = props;
    const primaryDevice = deviceStatus.find((device) => device.online) || deviceStatus[0] || null;
    const presenceLabel = statusMessage
        ? statusMessage
        : primaryDevice
            ? `${primaryDevice.deviceLabel || 'Desktop'} · ${primaryDevice.online ? 'online' : 'offline'}`
            : 'No desktop connected';
    const [lightboxImage, setLightboxImage] = useState(null);
    const friendlyStatus = prettySubscriptionStatus(subscription?.status);
    const recovery = getBillingRecovery(subscription?.status, entitled);
    const submit = async (event) => {
        event.preventDefault();
        if (cooldownActive)
            return;
        await onSend(composer);
    };
    return (_jsxs("div", { className: "chat-shell", children: [_jsxs("header", { className: "chat-bar", children: [_jsxs("div", { className: "chat-brand", children: [_jsx("span", { className: "dot" }), _jsx("span", { children: "Picnic Basket" })] }), _jsxs("div", { className: "chat-tabs", children: [_jsx("button", { className: activeTab === 'chat' ? 'tab active' : 'tab', type: "button", onClick: () => onSelectTab('chat'), children: "Chat" }), _jsx("button", { className: activeTab === 'subscription' ? 'tab active' : 'tab', type: "button", onClick: () => onSelectTab('subscription'), children: "Subscription" })] }), _jsxs("div", { className: "chat-meta", children: [_jsx("span", { className: "chat-email", children: user?.email || 'loading…' }), _jsx("span", { className: "chat-presence", children: presenceLabel })] })] }), activeTab === 'chat' ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "messages", children: [messages.length === 0 && _jsx("p", { className: "empty", children: "Start a conversation." }), messages.map((m, idx) => (_jsxs("div", { className: `bubble ${m.role}`, children: [m.content, m.attachments && m.attachments.length > 0 && (_jsx("div", { className: "bubble-attachments", children: m.attachments.map((att, attIdx) => (_jsxs("div", { className: "bubble-attachment-item", children: [att.previewUrl && att.contentType.startsWith('image/') ? (_jsx("button", { type: "button", className: "bubble-attachment-image-btn", onClick: () => setLightboxImage({ src: att.previewUrl, alt: att.name }), "aria-label": `Open image ${att.name}`, children: _jsx("img", { src: att.previewUrl, alt: att.name, className: "bubble-attachment-image" }) })) : (_jsx("div", { className: "bubble-attachment-file", children: "\uD83D\uDCCE" })), _jsxs("div", { className: "bubble-attachment-meta", children: [_jsx("span", { children: att.name }), _jsx("small", { children: formatBytes(att.sizeBytes) })] })] }, `${att.name}-${attIdx}`))) }))] }, `${m.runId || idx}-${idx}`)))] }), _jsxs("form", { onSubmit: submit, className: "composer", children: [cooldownActive && (_jsxs("div", { className: "composer-cooldown", role: "status", "aria-live": "polite", children: ["Cooldown active (", formatCooldownReason(cooldownReason), "). Try again in ", formatDurationShort(cooldownRemainingMs), "."] })), pendingFiles.length > 0 && (_jsx("div", { className: "composer-attachments", children: pendingFiles.map((file, idx) => (_jsxs("div", { className: "attachment-chip", children: [_jsx("span", { className: "attachment-chip-name", children: file.file.name }), _jsx("span", { className: "attachment-chip-size", children: formatBytes(file.file.size) }), _jsx("button", { type: "button", className: "attachment-chip-remove", onClick: () => onRemoveFile(idx), "aria-label": `Remove ${file.file.name}`, children: "\u00D7" })] }, `${file.file.name}-${file.file.lastModified}-${idx}`))) })), _jsxs("div", { className: "composer-row", children: [_jsx("input", { id: "composer-file-input", type: "file", multiple: true, onChange: (e) => {
                                            onAddFiles(e.target.files);
                                            e.currentTarget.value = '';
                                        }, className: "composer-file-input" }), _jsx("input", { id: "composer-camera-input", type: "file", accept: "image/*", capture: "environment", onChange: (e) => {
                                            onAddFiles(e.target.files);
                                            e.currentTarget.value = '';
                                        }, className: "composer-file-input" }), _jsx("button", { type: "button", className: "composer-tool", onClick: () => document.getElementById('composer-file-input')?.click(), "aria-label": "Attach files", children: "\uD83D\uDCCE" }), _jsx("button", { type: "button", className: "composer-tool", onClick: () => document.getElementById('composer-camera-input')?.click(), "aria-label": "Open camera", children: "\uD83D\uDCF7" }), _jsx("textarea", { rows: 2, value: composer, onChange: (e) => setComposer(e.target.value), placeholder: cooldownActive ? `Cooldown active — try again in ${formatDurationShort(cooldownRemainingMs)}` : 'Ask Picnic to do something…' }), _jsx("button", { className: "primary", type: "submit", disabled: cooldownActive || sending || (!composer.trim() && pendingFiles.length === 0), children: sending ? 'Sending…' : 'Send' })] })] })] })) : (_jsxs("div", { className: "messages subscription-panel", children: [_jsx("div", { className: entitled ? 'sub-banner ok' : 'sub-banner warn', children: entitled ? 'Your Picnic subscription is active.' : 'Subscription not active. Start or reactivate to continue.' }), !entitled && (_jsxs("div", { className: "sub-recovery", children: [_jsx("strong", { children: recovery.title }), _jsx("span", { children: recovery.detail })] })), _jsxs("div", { className: "sub-row", children: [_jsx("span", { children: "Plan" }), _jsx("strong", { children: subscription?.planName || 'Picnic Subscription' })] }), _jsxs("div", { className: "sub-row", children: [_jsx("span", { children: "Status" }), _jsx("strong", { children: friendlyStatus })] }), _jsxs("div", { className: "sub-row", children: [_jsx("span", { children: "Price" }), _jsxs("strong", { children: ["$", subscription?.priceUsdMonthly || 150, "/month"] })] }), _jsxs("div", { className: "sub-row", children: [_jsx("span", { children: "Renews" }), _jsx("strong", { children: subscription?.renewsAt ? new Date(subscription.renewsAt).toLocaleString() : 'Unknown' })] }), _jsxs("div", { className: "sub-row", children: [_jsx("span", { children: "Usage" }), _jsx("strong", { children: usage?.limit ? `${usage.used || 0} / ${usage.limit} per ${usage.cycle || 'month'}` : 'Not available yet' })] }), _jsxs("div", { className: "sub-usage-grid", children: [_jsxs("div", { className: "sub-usage-box", children: [_jsx("span", { children: "Hour" }), _jsx("strong", { children: formatWindowUsage(usage?.hour) })] }), _jsxs("div", { className: "sub-usage-box", children: [_jsx("span", { children: "Day" }), _jsx("strong", { children: formatWindowUsage(usage?.day) })] }), _jsxs("div", { className: "sub-usage-box", children: [_jsx("span", { children: "Week" }), _jsx("strong", { children: formatWindowUsage(usage?.week) })] })] }), _jsxs("div", { className: "sub-actions", children: [_jsx("button", { className: "primary", type: "button", onClick: () => void onBuySubscription(), children: entitled ? 'Upgrade / Change Plan' : 'Start Subscription' }), _jsx("button", { type: "button", onClick: () => void onManageBilling(), children: "Manage billing" }), _jsx("button", { type: "button", onClick: () => void onRefreshSubscription(), children: "Refresh" })] }), !entitled && (_jsx("p", { className: "status subtle", children: "Tip: if payment failed, open Manage billing to update your payment method, then press Refresh." })), _jsx("p", { className: "status subtle", children: "Sign in is handled by WorkOS and shared with Picnic Desktop." })] })), lightboxImage && (_jsx("div", { className: "image-lightbox", role: "dialog", "aria-modal": "true", onClick: () => setLightboxImage(null), children: _jsxs("div", { className: "image-lightbox-inner", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "image-lightbox-close", onClick: () => setLightboxImage(null), "aria-label": "Close image preview", children: "\u00D7" }), _jsx("img", { src: lightboxImage.src, alt: lightboxImage.alt, className: "image-lightbox-img" }), _jsx("p", { className: "image-lightbox-caption", children: lightboxImage.alt })] }) }))] }));
}
function formatBytes(size) {
    if (size < 1024)
        return `${size} B`;
    if (size < 1024 * 1024)
        return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
function formatWindowUsage(windowUsage) {
    if (!windowUsage || !windowUsage.limit)
        return '--';
    return `${windowUsage.used || 0} / ${windowUsage.limit}`;
}
function formatDurationShort(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0
        ? `${minutes}:${String(seconds).padStart(2, '0')}`
        : `${seconds}s`;
}
function formatCooldownReason(reason) {
    const normalized = String(reason || '').toLowerCase();
    if (normalized === 'pacing_limit')
        return 'usage pace';
    if (normalized === 'hour_limit')
        return 'hourly cap';
    if (normalized === 'day_limit')
        return 'daily cap';
    if (normalized === 'week_limit')
        return 'weekly cap';
    if (normalized === 'month_limit')
        return 'billing-cycle cap';
    if (normalized === 'cost_ceiling')
        return 'budget cap';
    return 'usage cap';
}
function deriveCooldownFromUsage(usage) {
    if (!usage)
        return null;
    const now = Date.now();
    const monthUsed = Number(usage.month?.used);
    const monthLimit = Number(usage.month?.limit);
    const usageCycleEnd = Number(usage.cycleEnd || 0);
    if (Number.isFinite(monthUsed) && Number.isFinite(monthLimit) && monthLimit > 0 && monthUsed >= monthLimit && usageCycleEnd > now) {
        return { until: usageCycleEnd, reason: 'month_limit' };
    }
    const availableNow = Number(usage.pacing?.availableNow);
    const refillPerHour = Number(usage.pacing?.refillPerHour);
    const pacingCycleEnd = Number(usage.pacing?.cycleEnd || usageCycleEnd || 0);
    if (Number.isFinite(availableNow) && availableNow < 1) {
        if (Number.isFinite(refillPerHour) && refillPerHour > 0) {
            const refillPerMs = refillPerHour / (60 * 60 * 1000);
            const shortfall = 1 - availableNow;
            const eta = now + Math.ceil(shortfall / refillPerMs);
            const until = pacingCycleEnd > now ? Math.min(eta, pacingCycleEnd) : eta;
            return { until, reason: 'pacing_limit' };
        }
        if (pacingCycleEnd > now) {
            return { until: pacingCycleEnd, reason: 'pacing_limit' };
        }
    }
    return null;
}
function prettySubscriptionStatus(status) {
    const normalized = String(status || 'unknown').toLowerCase();
    if (normalized === 'active')
        return 'Active';
    if (normalized === 'trialing')
        return 'Trial';
    if (normalized === 'past_due')
        return 'Payment overdue';
    if (normalized === 'canceled' || normalized === 'cancelled')
        return 'Canceled';
    if (normalized === 'revoked')
        return 'Revoked';
    if (normalized === 'not_subscribed' || normalized === 'none')
        return 'Not subscribed';
    if (normalized === 'billing_unavailable')
        return 'Billing unavailable';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
function getBillingRecovery(status, entitled) {
    if (entitled) {
        return { title: 'All good', detail: 'Your subscription is active.' };
    }
    const normalized = String(status || 'unknown').toLowerCase();
    if (normalized === 'past_due') {
        return {
            title: 'Payment overdue',
            detail: 'Update your payment method in Manage billing, then refresh status.',
        };
    }
    if (normalized === 'canceled' || normalized === 'cancelled' || normalized === 'revoked') {
        return {
            title: 'Subscription ended',
            detail: 'Start subscription again to restore paid access immediately.',
        };
    }
    if (normalized === 'billing_unavailable') {
        return {
            title: 'Billing temporarily unavailable',
            detail: 'Try Refresh in a moment. If this persists, open Manage billing to verify account state.',
        };
    }
    return {
        title: 'Not subscribed yet',
        detail: 'Start subscription to enable paid features and hosted usage.',
    };
}
function QrLinkNotice({ token, message }) {
    return (_jsx("div", { className: "login-shell", children: _jsxs("div", { className: "login-panel", children: [_jsx("p", { className: "status subtle", children: message || 'Linking this browser to Picnic…' }), _jsx("p", { className: "status subtle", children: "If you just scanned a QR from Picnic Desktop, keep this tab open\u2014it will refresh automatically." }), _jsxs("small", { className: "status subtle", style: { opacity: 0.4 }, children: ["token ", token.slice(0, 6), "\u2026"] })] }) }));
}
function parseRoute() {
    const path = window.location.pathname || '/';
    if (path.startsWith('/qr/')) {
        const token = path.split('/qr/')[1];
        if (token)
            return { route: 'qr', token: decodeURIComponent(token) };
    }
    if (path === '/login')
        return { route: 'login' };
    return { route: 'chat' };
}
function replacePath(path) {
    window.history.replaceState(null, '', path);
}
function toStoredSession(session) {
    return {
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        accessExpiresAt: Date.now() + session.expiresIn * 1000,
        refreshExpiresAt: Date.now() + session.refreshExpiresIn * 1000,
    };
}
